import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {SchemaCollaborator} from 'auth/PermissionConstants';
import {useSchemaCollaboratorsSection} from 'collaborator-components/useSchemaCollaboratorsSection';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as TestDefinitionDAO from 'sample-tests/network/TestDefinitionDAO';
import {SchemaWithOrgPermissionsSettings} from 'schemas/SchemaWithOrgPermissionsSettings';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  TestDefinitionPermissionSettingsQuery,
  TestDefinitionPermissionSettingsQueryVariables,
} from './__generated/TestDefinitionPermissionSettingsQuery';

type MaybeInaccessibleTestDefinition = TestDefinitionPermissionSettingsQuery['testDefinition'];

export type TestDefinitionWithPermissionSettings = ExtractByTypename<
  MaybeInaccessibleTestDefinition,
  'TestDefinitionNode'
>;

export const TEST_DEFINITION_PERMISSION_SETTINGS_QUERY = gql`
  query TestDefinitionPermissionSettingsQuery($testDefinitionId: StrictString!)
  @skipAudit
  @engComponent(name: SAMPLE_TESTS) {
    testDefinition(id: $testDefinitionId) {
      ... on PoliceableInterface {
        id
        permissions {
          ...SchemaPermissionsGraphQL
        }
        ...CollaboratorInfoGraphQL
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('CollaboratorInfo').fragment}
  ${getFragmentWrapper('SchemaPermissions').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function updateTestDefinitionCollaborators(
  testDefinitionId: string,
  data: {
    collaborators: Array<SchemaCollaborator>;
  }
): Promise<void> {
  const response = await TestDefinitionDAO.updateCollaborators(testDefinitionId, data);
  writeFragmentsToCache(getFragmentWrapper('CollaboratorInfo'), [response.testDefinition]);
}

export interface QueryingProps {
  testDefinitionId: string;
}
function QueryingTestDefinitionPermissionSettings({testDefinitionId}: QueryingProps): JSX.Element | null {
  const result = useBenchlingQuery<
    TestDefinitionPermissionSettingsQuery,
    TestDefinitionPermissionSettingsQueryVariables
  >(TEST_DEFINITION_PERMISSION_SETTINGS_QUERY, {
    variables: {testDefinitionId},
  });
  if (result.type === QueryResultType.ERROR) {
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    assert(!isInaccessible(result.data.testDefinition), 'Expected test definition to be accessible.');
    return (
      <ExistingTestDefinitionPermissionSettings
        testDefinition={result.data.testDefinition}
        refetch={async () => {
          await result.refetch();
        }}
      />
    );
  }
}

export function ExistingTestDefinitionPermissionSettings({
  testDefinition,
  refetch,
}: {
  testDefinition: TestDefinitionWithPermissionSettings;
  refetch: () => Promise<void>;
}): JSX.Element {
  const [{collaborators, minimalCollaborators}, handleUpdate] = useSchemaCollaboratorsSection(
    testDefinition.collaboratorsJson
  );
  return (
    <SchemaWithOrgPermissionsSettings
      id={testDefinition.id}
      isReadOnly={!testDefinition.permissions.definitionEditable}
      collaborators={collaborators}
      onUpdate={handleUpdate}
      onSubmit={async () => {
        await updateTestDefinitionCollaborators(testDefinition.id, {
          collaborators: minimalCollaborators,
        });
        refetch();
      }}
    />
  );
}

export default QueryingTestDefinitionPermissionSettings;
